/**
 * `NetworkGameSession` — client-side game session over a pluggable transport.
 *
 * Owns a single network connection to the server, drives the handshake,
 * handles RPCs and replication packets, and exposes them through the
 * `IGameSession` delegates.
 *
 * The default transport is WebSocket; pass a `NetworkConnectionFactory`
 * to swap it (e.g. for tests or WebTransport).
 */
import { NET_PING_INTERVAL_SEC } from '../../const.js';
import { shortUUID } from '../../utils/uuid.js';
import { IGameSession } from '../interface.js';

import { AbstractMeteredConnection } from './AbstractMeteredConnection.js';
import {
  decodeWireMessage,
  encodeWireMessage,
  type HandshakeRequestData,
  type HandshakeResponseData,
  MessageType,
  type PingMessage,
  type ReplicationAckMessage,
  type ReplicationMessage,
  type RequestMessage,
  type ResponseMessage,
  type RpcMessage
} from './NetworkProtocol.js';

import type {
  ClientState,
  ConnectionStatsSnapshot,
  INetworkConnection,
  JoinParams,
  ReplicationAck
} from '../interface.js';
import type { ClientId, ObjectId } from '../interface.js';

/** Factory that produces an {@link INetworkConnection} bound to a server URL. */
export type NetworkConnectionFactory = (url: string) => INetworkConnection;

/**
 * Default {@link INetworkConnection} implementation backed by the browser/Node
 * WebSocket. Used by {@link NetworkGameSession} when no custom factory is
 * provided.
 */
export class WebSocketNetworkConnection extends AbstractMeteredConnection {
  private ws: WebSocket | null = null;

  constructor(private readonly url: string) {
    super();
  }

  public isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  public connect(): void {
    const ws = new WebSocket(this.url);
    ws.binaryType = 'arraybuffer';
    this.ws = ws;

    ws.onopen = () => {
      this.onOpen.invoke();
    };

    ws.onmessage = (event: MessageEvent<ArrayBuffer | Blob>) => {
      const { data } = event;
      if (data instanceof ArrayBuffer) {
        const bytes = new Uint8Array(data);
        this.trackReceived(bytes.byteLength);
        this.onMessage.invoke(bytes);
        return;
      }
      if (typeof Blob !== 'undefined' && data instanceof Blob) {
        void data.arrayBuffer().then((buf) => {
          const bytes = new Uint8Array(buf);
          this.trackReceived(bytes.byteLength);
          this.onMessage.invoke(bytes);
        });
        return;
      }
      console.warn('[WebSocketNetworkConnection] Ignoring unexpected WebSocket message type');
    };

    ws.onclose = (event) => {
      this.onClose.invoke(event.code, event.reason);
    };

    ws.onerror = (event) => {
      const message = event instanceof ErrorEvent ? event.message : 'Network connection error';
      this.onError.invoke(new Error(message));
    };
  }

  public send(data: Uint8Array): void {
    if (!this.ws) {
      return;
    }
    this.trackSent(data.byteLength);
    this.ws.send(data);
  }

  public close(code?: number, reason?: string): void {
    this.ws?.close(code, reason);
  }

  protected override collectExtra(): Record<string, number> | undefined {
    if (!this.ws) {
      return undefined;
    }
    return {
      bufferedAmount: this.ws.bufferedAmount,
      readyState: this.ws.readyState
    };
  }
}

/**
 * Options for constructing a {@link NetworkGameSession}.
 */
export interface NetworkGameSessionOptions {
  /** WebSocket / WebTransport / etc. URL of the server to connect to. */
  serverUrl: string;
  /** Optional join params forwarded to `GameMode.canPlayerJoin()` on the server. */
  joinParams?: JoinParams;
  /** Optional transport factory; defaults to a browser WebSocket. */
  connectionFactory?: NetworkConnectionFactory;
  /** Optional client-reported engine version (diagnostic). */
  clientEngineVersion?: string;
  /** Optional client-reported game version (diagnostic). */
  clientGameVersion?: string;
}

/**
 * Pending request slot kept while we wait for the matching response.
 */
interface PendingRequest {
  resolve: (value: any) => void;
  reject: (error: Error) => void;
}

/**
 * Client-side `IGameSession` implementation.
 *
 * The session is "ready" once the WebSocket is open and the server has
 * responded to the handshake. Call {@link waitUntilReady} to await both.
 */
export class NetworkGameSession extends IGameSession {
  private readonly serverUrl: string;
  private readonly connectionFactory: NetworkConnectionFactory;
  private readonly joinParams: JoinParams;
  private readonly clientEngineVersion: string | undefined;
  private readonly clientGameVersion: string | undefined;

  private connection: INetworkConnection;
  private pendingRequests: Map<string, PendingRequest> = new Map();
  private isConnected: boolean = false;
  private connectionReady: Promise<void>;
  private connectionResolve?: () => void;
  private connectionReject?: (error: Error) => void;
  private handshakeReady: Promise<void>;
  private handshakeResolve?: () => void;
  private handshakeReject?: (error: Error) => void;
  private reconnectAttempts: number = 0;
  private readonly maxReconnectAttempts: number = 5;
  private readonly reconnectDelay: number = 1000;
  private assignedClientId: ClientId | null = null;
  private intentionalDisconnect: boolean = false;
  private pingTimer: ReturnType<typeof setInterval> | null = null;

  constructor(options: NetworkGameSessionOptions) {
    // Until the handshake response arrives we only know the URL; the
    // description gets replaced with the server-provided one in
    // handleHandshake().
    super({
      gameId: '',
      sessionName: undefined
    });

    this.serverUrl = options.serverUrl;
    this.connectionFactory = options.connectionFactory ?? ((url) => new WebSocketNetworkConnection(url));
    this.joinParams = options.joinParams ?? {};
    this.clientEngineVersion = options.clientEngineVersion;
    this.clientGameVersion = options.clientGameVersion;

    this.connectionReady = new Promise((resolve, reject) => {
      this.connectionResolve = resolve;
      this.connectionReject = reject;
    });
    this.handshakeReady = new Promise((resolve, reject) => {
      this.handshakeResolve = resolve;
      this.handshakeReject = reject;
    });
    this.connection = this.createConnection();
    this.connection.connect();
  }

  //==================================================================
  // IGameSession overrides
  //==================================================================

  public override async waitUntilReady(): Promise<void> {
    await this.connectionReady;
    await this.handshakeReady;
  }

  public override getAssignedClientId(): ClientId | null {
    return this.assignedClientId;
  }

  public override getConnectionStats(): ConnectionStatsSnapshot | null {
    return this.connection?.getStats() ?? null;
  }

  public override async clientHeartBeat(clientId: ClientId): Promise<boolean> {
    return this.sendRequest<boolean>(MessageType.CLIENT_HEARTBEAT, { clientId });
  }

  public override async getClients(): Promise<Record<ClientId, ClientState>> {
    return this.sendRequest<Record<ClientId, ClientState>>(MessageType.GET_CLIENTS, {});
  }

  public override sendServerRpc(
    objectId: ObjectId,
    methodName: string,
    args: any[],
    reliable: boolean
  ): void {
    void this.connectionReady.then(() => {
      if (!this.isConnected) {
        console.warn('[NetworkGameSession] Cannot send server RPC - not connected');
        return;
      }
      const message: RpcMessage = {
        type: MessageType.RPC,
        id: shortUUID(),
        objectId,
        methodName,
        args,
        reliable
      };
      try {
        this.connection.send(encodeWireMessage(message));
      } catch (error) {
        console.error('[NetworkGameSession] Failed to send server RPC:', error);
      }
    });
  }

  public override sendReplicationAck(ack: ReplicationAck): void {
    if (!this.isConnected) {
      console.warn('[NetworkGameSession] Cannot send replication ack - not connected');
      return;
    }
    const message: ReplicationAckMessage = {
      type: MessageType.REPLICATION_ACK,
      data: ack
    };
    try {
      this.connection.send(encodeWireMessage(message));
    } catch (error) {
      console.error('[NetworkGameSession] Failed to send replication ack:', error);
    }
  }

  public override destroy(): void {
    this.stopTransportPingLoop();
    this.intentionalDisconnect = true;
    this.close();
  }

  //==================================================================
  // Transport / handshake plumbing
  //==================================================================

  /**
   * Gracefully disconnect from the server. Sends a DISCONNECT request and
   * waits for the server's acknowledgment; falls back to a hard close if
   * the request fails.
   */
  public async disconnect(): Promise<void> {
    if (!this.isConnected) {
      console.log('[NetworkGameSession] Not connected, skipping disconnect');
      return;
    }

    this.stopTransportPingLoop();
    this.intentionalDisconnect = true;

    try {
      console.log('[NetworkGameSession] Sending disconnect request to server');
      await this.sendRequest<void>(MessageType.DISCONNECT, {});
      console.log('[NetworkGameSession] Server acknowledged disconnect');
    } catch (error) {
      console.warn('[NetworkGameSession] Disconnect request failed, performing hard close:', error);
    } finally {
      this.close();
    }
  }

  /**
   * Close the underlying connection without sending DISCONNECT. Used both by
   * {@link disconnect} and as a hard-close path.
   */
  public close(): void {
    this.connection.close();
    this.pendingRequests.clear();
  }

  /**
   * Send a request through the connection and resolve with the server's
   * response payload. Times out after 10 seconds.
   */
  private async sendRequest<T = any>(type: MessageType, data?: any): Promise<T> {
    await this.connectionReady;

    return new Promise<T>((resolve, reject) => {
      if (!this.isConnected) {
        reject(new Error('Network connection is not connected'));
        return;
      }

      const requestId = shortUUID();
      const message: RequestMessage = { type, id: requestId, data };
      this.pendingRequests.set(requestId, { resolve, reject });

      try {
        this.connection.send(encodeWireMessage(message));
      } catch (error) {
        this.pendingRequests.delete(requestId);
        reject(error as Error);
      }

      setTimeout(() => {
        if (this.pendingRequests.has(requestId)) {
          this.pendingRequests.delete(requestId);
          reject(new Error('Request timeout'));
        }
      }, 10000);
    });
  }

  private createConnection(): INetworkConnection {
    const connection = this.connectionFactory(this.serverUrl);

    connection.onOpen.add(() => {
      console.log(`[NetworkGameSession] Connected to ${this.serverUrl}`);
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.connectionResolve?.();
      this.connectionResolve = undefined;
      this.connectionReject = undefined;

      // Initiate the handshake immediately after the socket opens.
      this.sendHandshakeRequest();
    });

    connection.onMessage.add((rawMessage) => {
      try {
        const message = decodeWireMessage(rawMessage);
        this.handleMessage(message);
      } catch (error) {
        console.error('[NetworkGameSession] Failed to decode network message:', error);
      }
    });

    connection.onClose.add((code, reason) => {
      console.log('[NetworkGameSession] Connection closed:', code, reason);
      this.isConnected = false;
      this.stopTransportPingLoop();
      this.handleReconnection();
    });

    connection.onError.add((error) => {
      console.error('[NetworkGameSession] Connection error:', error.message);
      if (this.connectionReject && !this.isConnected) {
        this.connectionReject(new Error('Failed to connect to network server'));
        this.connectionResolve = undefined;
        this.connectionReject = undefined;
      }
    });

    return connection;
  }

  /**
   * Send the handshake request once the WebSocket is open. The server uses
   * `joinParams` to gate the join and fires `handlePlayerJoined` as part of
   * its response.
   */
  private sendHandshakeRequest(): void {
    const data: HandshakeRequestData = {
      joinParams: this.joinParams,
      clientEngineVersion: this.clientEngineVersion,
      clientGameVersion: this.clientGameVersion
    };
    const message: RequestMessage = {
      type: MessageType.HANDSHAKE,
      id: 'handshake',
      data
    };
    try {
      this.connection.send(encodeWireMessage(message));
    } catch (error) {
      console.error('[NetworkGameSession] Failed to send handshake request:', error);
      this.handshakeReject?.(error as Error);
      this.handshakeResolve = undefined;
      this.handshakeReject = undefined;
    }
  }

  private handleReconnection(): void {
    if (this.intentionalDisconnect) {
      console.log('[NetworkGameSession] Intentional disconnect, not reconnecting');
      return;
    }

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`[NetworkGameSession] Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);

      this.connectionReady = new Promise((resolve, reject) => {
        this.connectionResolve = resolve;
        this.connectionReject = reject;
      });
      this.handshakeReady = new Promise((resolve, reject) => {
        this.handshakeResolve = resolve;
        this.handshakeReject = reject;
      });

      setTimeout(() => {
        this.connection = this.createConnection();
        this.connection.connect();
      }, this.reconnectDelay * this.reconnectAttempts);
    } else {
      console.error('[NetworkGameSession] Max reconnection attempts reached. Connection failed.');
      this.pendingRequests.forEach(({ reject }) => {
        reject(new Error('Network connection lost'));
      });
      this.pendingRequests.clear();
    }
  }

  /**
   * Start the periodic transport PING so connection stats accumulate RTT
   * samples after the handshake completes.
   */
  private startTransportPingLoop(): void {
    this.stopTransportPingLoop();
    this.pingTimer = setInterval(() => {
      this.sendPing();
    }, NET_PING_INTERVAL_SEC * 1000);
    this.sendPing();
  }

  private stopTransportPingLoop(): void {
    if (this.pingTimer !== null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  /**
   * Fire-and-forget PING. RTT is recorded when the matching PONG arrives.
   */
  public sendPing(): void {
    if (!this.isConnected) {
      return;
    }
    const ping: PingMessage = {
      type: MessageType.PING,
      pingId: shortUUID(),
      sentAt: Date.now()
    };
    try {
      this.connection.send(encodeWireMessage(ping));
    } catch (error) {
      console.warn('[NetworkGameSession] Failed to send ping:', error);
    }
  }

  /**
   * Dispatch an inbound wire message to the appropriate handler.
   */
  private handleMessage(message: any): void {
    if (message?.type === MessageType.PING) {
      const ping = message as PingMessage;
      const pong: PingMessage = {
        type: MessageType.PONG,
        pingId: ping.pingId,
        sentAt: ping.sentAt
      };
      try {
        this.connection.send(encodeWireMessage(pong));
      } catch (error) {
        console.warn('[NetworkGameSession] Failed to send pong:', error);
      }
      return;
    }
    if (message?.type === MessageType.PONG) {
      const pong = message as PingMessage;
      this.connection.recordRttSample(Date.now() - pong.sentAt);
      return;
    }

    if (message?.type === MessageType.HANDSHAKE) {
      this.handleHandshake(message as ResponseMessage);
      return;
    }

    if (message?.type === MessageType.REPLICATION) {
      const envelope = message as ReplicationMessage;
      if (envelope.data) {
        this.onReplicationPacketReceived.invoke(envelope.data);
      }
      return;
    }

    // Correlated responses (`id` + `success`), including RPC acks where the
    // server reuses `type: rpc` — handle before true RPC invocations below.
    if (message?.id !== undefined && typeof message.success === 'boolean') {
      const pending = this.pendingRequests.get(message.id);
      if (pending) {
        this.pendingRequests.delete(message.id);
        if (message.success) {
          pending.resolve(message.data);
        } else {
          pending.reject(new Error(message.error ?? 'Unknown error'));
        }
      }
      return;
    }

    if (message?.type === MessageType.RPC) {
      const rpc = message as RpcMessage;
      this.onRPCReceived.invoke(
        {
          objectId: rpc.objectId,
          methodName: rpc.methodName,
          args: rpc.args,
          reliable: rpc.reliable,
          targetClients: rpc.targetClients
        },
        rpc.senderId
      );
      return;
    }
  }

  private handleHandshake(response: ResponseMessage): void {
    if (!response.success || !response.data) {
      this.stopTransportPingLoop();
      const error = new Error(response.error ?? 'Handshake failed');
      this.handshakeReject?.(error);
      this.handshakeResolve = undefined;
      this.handshakeReject = undefined;
      return;
    }

    const data = response.data as HandshakeResponseData;
    this.assignedClientId = data.clientId;
    this.description = data.gameSession;

    // Apply state from the description so getDescription() reflects what the
    // server just told us (player count, state, etc.).
    if (data.gameSession.state !== undefined) {
      this.state = data.gameSession.state;
    }
    if (data.gameSession.playerCount !== undefined) {
      this.playerCount = data.gameSession.playerCount;
    }
    if (data.gameSession.maxPlayers !== undefined) {
      this.maxPlayers = data.gameSession.maxPlayers;
    }
    if (data.gameSession.countdown !== undefined) {
      this.countdown = data.gameSession.countdown;
    }

    console.log(`[NetworkGameSession] Handshake complete: clientId=${this.assignedClientId}, server=${data.engineVersion}/${data.gameVersion}, game=${data.gameSession.gameId}`);

    this.handshakeResolve?.();
    this.handshakeResolve = undefined;
    this.handshakeReject = undefined;

    this.startTransportPingLoop();
  }

  /**
   * Expose a description-fetcher for the server URL the client connected to.
   */
  public getServerUrl(): string {
    return this.serverUrl;
  }
}
